import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  isDevMode,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef
} from '@angular/core';
import {ColumnAlign, ColumnConfig, ColumnView, HighlightStyleConfig, RowValue, TableConfig} from './model/ColumnConfig';
import {StyleBuilder} from './style-builder/style-builder';
import {MatSort, Sort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {SelectionProvider, SelectionProviderDummy} from './providers/SelectionProvider';
import {ActionColumnView, ActionEvent, RowActionProvider, RowActionProviderDummy} from './providers/RowActionProvider';
import {TableRow} from "./model/TableRow";
import {TableViewFactory} from "./model/TableViewFactory";
import {ResolvedRowStyle, RowStyleFactory} from "./model/RowStyleFactory";
import {IndexProvider, IndexProviderDummy} from "./providers/IndexProvider";
import {PaginationProvider, PaginationProviderDummy} from "./providers/PaginationProvider";
import {TableRowsFactory} from "./factories/TableRowsFactory";
import {DisplayColumnsFactory} from "./factories/DisplayColumnsFactory";
import {TotalRowProvider, TotalRowProviderDummy} from "./providers/TotalRowProvider";
import { RowLinkProvider, RowLinkProviderDummy } from './providers/RowLinkProvider';
import {NgxAurFilters} from "./filters/NgxAurFilters";
import {NgxAurMatTablePublic} from "./ngx-aur-mat-table-public";
import {OffsetUtil} from "./utils/offset.util";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {NgxTableSubFooterRowDirective} from "./directive/ngx-table-sub-footer-row.directive";
import {SelectionModel} from "@angular/cdk/collections";
import {HeaderButtonProvider, HeaderButtonProviderDummy} from "./providers/HeaderButtonProvider";
import {DragDropProvider, DragProviderDummy} from "./providers/DragDropProvider";
import {TimelineProvider, TimelineProviderDummy} from "./providers/TimelineProvider";
import {AurDragDropComponent} from "./drag-drop/aur-drag-drop-component";
import {PaginatorState} from './model/PaginatorState';
export {PaginatorState} from './model/PaginatorState';
import {AurPageLoadedEvent, AurPageSource} from './model/AurPage';
import {ServerPageController} from './providers/ServerPageController';
import { isFeatureEnabled as isFeatureEnabledFn } from './utils/feature-enabled.util';
import { NgxAurTableConfigUtil } from './utils/ngx-aur-table-config.util';
import {Subscription} from 'rxjs';
import {NgxAurCellDefDirective} from './directive/ngx-aur-cell-def.directive';
import {AurCellContext} from './model/AurCellContext';
import {NgxAurExpandedRowDefDirective} from './directive/ngx-aur-expanded-row-def.directive';
import {NgxAurRowMarkerDefDirective} from './directive/ngx-aur-row-marker-def.directive';
import {AurRowContext} from './model/AurRowContext';
import {NgxAurExtraHeaderTopDefDirective} from './directive/ngx-aur-extra-header-top-def.directive';
import {NgxAurExtraHeaderBottomDefDirective} from './directive/ngx-aur-extra-header-bottom-def.directive';
import {AurExtraHeaderContext} from './model/AurExtraHeaderContext';
import {NgxAurHeaderCellDefDirective} from './directive/ngx-aur-header-cell-def.directive';
import {AurHeaderCellContext} from './model/AurHeaderCellContext';

export interface ColumnOffset {
  left: number,
  width: number,
  key: string
}

enum ExpandState {
  COLLAPSED = 'collapsed',
  EXPANDED = 'expanded'
}

@Component({
    selector: 'aur-mat-table',
    templateUrl: './ngx-aur-mat-table.component.html',
    styleUrls: ['./ngx-aur-mat-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('detailExpand', [
            state(ExpandState.COLLAPSED, style({ height: '0px', minHeight: '0' })),
            state(ExpandState.EXPANDED, style({ height: '*' })),
            transition(`${ExpandState.EXPANDED} <=> ${ExpandState.COLLAPSED}`, animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false
})
export class NgxAurMatTableComponent<T> implements OnInit, OnChanges, AfterContentInit, AfterViewInit, OnDestroy, NgxAurMatTablePublic<T>, AurDragDropComponent<TableRow<T>> {

  expandedStateEnum = ExpandState;

  readonly EXTRA_HEADER_CELL_TOP_SUFFIX = '_extra_header_cell_top';
  readonly EXTRA_HEADER_CELL_BOTTOM_SUFFIX = '_extra_header_cell_bottom';

  public tableDataSource = new MatTableDataSource<TableRow<T>>([]);
  _displayColumns: string[] = [];
  _displayExtraHeaderTopCell: string[] = [];
  _displayExtraHeaderBottomCell: string[] = [];

  _customDisplayColumnsEnabled = false;

  _tableName = 'unknown-table-name';

  @Input() set displayColumns(columns: string[]) {
    if (columns) {
      this._displayColumns = [...columns];
      this._customDisplayColumnsEnabled = columns && columns.length > 0;
    }
  }

  tableView: Map<string, ColumnView<string>>[] = [];

  private rowStyles: ResolvedRowStyle[] = [];

  _headerStyle: string | null = null;
  _headerClass: string | null = null;
  _totalStyle: string | null = null;
  _totalClass: string | null = null;

  /**
   * Классы выравнивания по ключу колонки; 'left'/не задан → undefined (без класса).
   * Пересобирается в initTable() — как и _headerStyle/_headerClass/rowStyles, реагирует
   * на смену [tableData]/[displayColumns]; смена ОДНОГО лишь [tableConfig] (существующее
   * ограничение компонента) пересборку не запускает.
   */
  _alignClass: Record<string, 'aur-align-center' | 'aur-align-right' | undefined> = {};

  /** Строки интерактивны (clickCfg задан) → tabindex/клавиатурная активация. */
  _rowsInteractive = false;

  /** Смещение индекса строки на номер страницы в серверном режиме (pageIndex*pageSize); 0 в клиентском. */
  _indexPageOffset = 0;

  @ContentChild(NgxTableSubFooterRowDirective) subFooterRowTemplate: TemplateRef<any> | null | undefined;

  @ContentChildren(NgxAurCellDefDirective, {descendants: true})
  cellDefs!: QueryList<NgxAurCellDefDirective>;

  /** key → шаблон тела ячейки, собранный из спроецированных ngxAurCellDef. */
  _cellTemplates = new Map<string, TemplateRef<any>>();
  private cellDefsSub?: Subscription;

  @ContentChildren(NgxAurHeaderCellDefDirective, {descendants: true})
  headerCellDefs!: QueryList<NgxAurHeaderCellDefDirective>;

  /** key → деф заголовка (templateRef + ownsCell), собранный из спроецированных ngxAurHeaderCellDef. */
  _headerCellDefs = new Map<string, NgxAurHeaderCellDefDirective>();
  private headerCellDefsSub?: Subscription;

  @ContentChildren(NgxAurExpandedRowDefDirective, {descendants: true})
  private expandedRowDefs!: QueryList<NgxAurExpandedRowDefDirective>;
  private _expandedRowTpl: TemplateRef<AurRowContext<T>> | null = null;
  get expandedRowTemplate(): TemplateRef<AurRowContext<T>> | null { return this._expandedRowTpl; }

  @ContentChildren(NgxAurRowMarkerDefDirective, {descendants: true})
  private rowMarkerDefs!: QueryList<NgxAurRowMarkerDefDirective>;
  private _rowMarkerTpl: TemplateRef<AurRowContext<T>> | null = null;
  get rowMarkerTemplate(): TemplateRef<AurRowContext<T>> | null { return this._rowMarkerTpl; }

  @ContentChildren(NgxAurExtraHeaderTopDefDirective, {descendants: true})
  private extraHeaderTopDefs!: QueryList<NgxAurExtraHeaderTopDefDirective>;
  private _extraHeaderTopTpl: TemplateRef<AurExtraHeaderContext> | null = null;
  get extraHeaderTopTemplate(): TemplateRef<AurExtraHeaderContext> | null { return this._extraHeaderTopTpl; }

  @ContentChildren(NgxAurExtraHeaderBottomDefDirective, {descendants: true})
  private extraHeaderBottomDefs!: QueryList<NgxAurExtraHeaderBottomDefDirective>;
  private _extraHeaderBottomTpl: TemplateRef<AurExtraHeaderContext> | null = null;
  get extraHeaderBottomTemplate(): TemplateRef<AurExtraHeaderContext> | null { return this._extraHeaderBottomTpl; }

  /** Подписки на .changes собранных template-директив (отписка в ngOnDestroy). */
  private defSubs: Subscription[] = [];

  // @ts-ignore
  @ViewChildren('rowLink', {read: ElementRef}) rows: QueryList<ElementRef>;
  // @ts-ignore
  @ViewChild('table', {read: ElementRef}) table: ElementRef;

  // @ts-ignore
  @Input() tableConfig: TableConfig<T>;

  @Input() tableData: T[] = [];

  @Input() expandedRow: T | null = null;
  @Output() expandedRowChange = new EventEmitter<T | null>();

  @Input() expandedRows: T[] = [];
  @Output() expandedRowsChange = new EventEmitter<T[]>();

  // если используется серверный пагинатор, сюда передается текущее состояние пагинатора
  @Input() paginatorState: PaginatorState | undefined;

  // Декларативный загрузчик в серверном режиме. Если задан, таблица сама управляет циклом загрузки.
  // @ts-ignore
  @Input() pageSource?: AurPageSource<T>;

  // Опциональный пагинатор, которым владеет хост и который размещён в другом месте разметки хоста.
  // @ts-ignore
  @Input() externalPaginator?: MatPaginator;

  @Input() isTableBodyHide = false;

  // @ts-ignore
  @ViewChild(MatPaginator, {static: false}) matPaginator: MatPaginator;
  // @ts-ignore
  @ViewChild(MatSort, {static: true}) matSort: MatSort;

  get activePaginator(): MatPaginator {
    return this.externalPaginator ?? this.matPaginator;
  }

  @Output() sort: EventEmitter<Sort> = new EventEmitter();

  @Output() pageChange = new EventEmitter<PageEvent>();

  // события, если действия включены
  @Output() rowAction: EventEmitter<ActionEvent<T>> = new EventEmitter<ActionEvent<T>>();
  // -----------------------

  // события, если включено событие выделения
  @Output() selectChange = new EventEmitter<T[]>();
  @Output() selectAdded = new EventEmitter<T[]>();
  @Output() selectRemoved = new EventEmitter<T[]>();

  @Output() selectedRowsAction = new EventEmitter<ActionEvent<T[]>>();

  @Output() selectionModel = new EventEmitter<SelectionModel<T>>();
  //------------------------

  @Output() rowClick = new EventEmitter<T>();

  @Output() loadingChange = new EventEmitter<boolean>();
  @Output() pageError = new EventEmitter<unknown>();

  /**
   * Успешно загруженная и УЖЕ применённая серверная страница (pageSource-режим).
   * Эмитится на каждую успешную загрузку: старт, смена страницы, сортировка, reload().
   * При ошибке не эмитится (см. pageError). В ручном/legacy режиме события нет —
   * хост загружает данные сам.
   */
  @Output() pageLoaded = new EventEmitter<AurPageLoadedEvent<T>>();

  /**
   * возвращает отфильтрованные строки
   */
  @Output() filterChange = new EventEmitter<T[]>();

  /** @deprecated используйте ngxAurExtraHeaderTopDef или ngxAurExtraHeaderBottomDef */
  @Output() columnOffsets = new EventEmitter<ColumnOffset[]>();
  private prevColumnOffsets: ColumnOffset[] = [];

  headerButtonProvider = new HeaderButtonProviderDummy();
  @Output() headerButton = new EventEmitter<MouseEvent>();

  private resizeColumnOffsetsObserver?: ResizeObserver;

  dragDropProvider: DragDropProvider<T> = new DragProviderDummy();

  timelineProvider: TimelineProvider<T> = new TimelineProviderDummy();
  _timelineFirstId = -1;
  _timelineLastId = -1;
  _timelineGaps = new Map<number, { topGap: boolean; bottomGap: boolean; topColor: string | null; bottomColor: string | null }>();

  selectionProvider: SelectionProvider<T> = new SelectionProviderDummy();

  rowActionsProvider: RowActionProvider<T> = new RowActionProviderDummy<T>();

  indexProvider: IndexProvider = new IndexProviderDummy();

  paginationProvider: PaginationProvider = new PaginationProviderDummy();

  private serverPageController?: ServerPageController<T>;
  private externalPaginatorSub?: Subscription;

  totalRowProvider: TotalRowProvider<T> = new TotalRowProviderDummy();

  rowLinkProvider: RowLinkProvider<T> = new RowLinkProviderDummy<T>();

  highlighted: T | undefined;

  /** Состояние раскрытия detail-строк: ключ идентичности → исходный объект (rowSrc). */
  private _expanded = new Map<unknown, T>();

  // Состояние наведения. Сравнивает идентичность объекта TableRow (тот же экземпляр, по которому итерирует шаблон),
  // в отличие от `highlighted`, который сравнивает row.rowSrc (внешнее @Input-значение, а не TableRow).
  hovered: TableRow<T> | null = null;

  private customSortFunctions = new Map<string, (data: TableRow<T>, key: string) => any>();

  private filterStorage = new Map<string, NgxAurFilters.Base<T>>();
  private _searchText = '';
  private _defaultFilterPredicate: (data: TableRow<T>, filter: string) => boolean;

  @Input()  highlightedRow: T | null = null;
  @Output() highlightedRowChange = new EventEmitter<T | null>();

  constructor(private viewContainerRef: ViewContainerRef,
              private cdr: ChangeDetectorRef) {
    // поиск только по значениям колонок (valueConverter) — дефолтный предикат Material
    // конкатенирует ВСЕ поля TableRow, включая служебные id и rowSrc ("[object Object]")
    this._defaultFilterPredicate = (data, filter) => this.searchPredicate(data, filter);
  }

  /** Предикат строки поиска: значения сконфигурированных колонок, без служебных полей. */
  private searchPredicate(data: TableRow<T>, filter: string): boolean {
    const needle = filter.trim().toLowerCase();
    const haystack = this.tableConfig.columnsCfg
      .map(c => data[c.key] ?? '')
      .join('◬')
      .toLowerCase();
    return haystack.includes(needle);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['tableData'] && this.tableData) || (changes['displayColumns'] && this._displayColumns)) {
      this.refreshTable();
    }
    if (changes['highlightedRow']) {
      const mode = this.tableConfig.bodyRowCfg?.highlightCfg?.mode ?? 'row-click';
      const authoritative = mode === 'controlled' || mode === 'manual';
      const firstSeed = mode === 'row-click'
        && changes['highlightedRow'].previousValue == null
        && this.highlightedRow != null;
      if (authoritative || firstSeed) {
        this.syncHighlightFromInput();
      }
    }
    if (changes['expandedRow'] || changes['expandedRows']) {
      const mode = this.tableConfig.extendedRowCfg?.mode ?? 'row-click';
      const multiple = !!this.tableConfig.extendedRowCfg?.multiple;
      const authoritative = mode === 'controlled' || mode === 'manual';
      const firstSeed = mode === 'row-click'
        && (!!changes['expandedRow']?.firstChange || !!changes['expandedRows']?.firstChange);
      if (authoritative || firstSeed) {
        this.syncExpandedFromInputs(multiple);
      }
    }
    if (changes['externalPaginator']) {
      if (this.externalPaginator) {
        if (this.isServerMode()) {
          // Серверный режим: НЕ привязываем dataSource.paginator (он порезал бы загруженную страницу).
          // (Пере)подключаем только после того, как контроллер создан; первое изменение, пришедшее до
          // ngAfterViewInit, обрабатывается в startServerController().
          if (this.serverPageController) {
            this.subscribeExternalPaginator();
            if (this.paginatorState) {
              this.applyExternalPaginatorState(this.paginatorState);
            }
          }
        } else if (!changes['externalPaginator'].firstChange) {
          // Клиентский режим: привязываем внешний пагинатор, чтобы MatTableDataSource резал данные через него.
          // Защищаемся от firstChange, чтобы не вызвать initPaginator() до ngAfterViewInit.
          this.initPaginator();
        }
      } else {
        // Внешний пагинатор удалён: отписываемся от его событий страницы, чтобы избежать утечки.
        this.externalPaginatorSub?.unsubscribe();
        this.externalPaginatorSub = undefined;
        // Клиентский режим откатывается к (теперь отрисованному) встроенному пагинатору.
        if (!this.isServerMode() && !changes['externalPaginator'].firstChange) {
          this.initPaginator();
        }
      }
    }
  }

  public resetPaginatorPageIndex() {
    if (this.activePaginator) {
      this.activePaginator.firstPage();
    }
  }

  public refreshTable() {
    const selected = this.selectionProvider?.selection?.selected ?? [];
    this.tableData = this.tableData || [];
    this.prepareTableData(selected);
  }

  ngOnInit(): void {
    if (!this.tableConfig) {
      throw new Error("init inputs [tableConfig] is mandatory!")
    }
    this.assertNoReservedColumnKeys();
    this.warnPaginationModeMisconfig();
    if (this.isServerMode() && !this.paginatorState) {
      this.paginatorState = PaginatorState.empty();
    }
  }

  /**
   * Ключ колонки не должен совпадать со служебным полем TableRow: иначе overlay
   * `row[c.key] = valueConverter(...)` затирает его (как было с `id` до 20.0.0).
   */
  private assertNoReservedColumnKeys(): void {
    const RESERVED = ['rowId', 'rowSrc'];
    const clash = this.tableConfig.columnsCfg.find(c => RESERVED.includes(c.key));
    if (clash) {
      throw new Error(
        `[aur-mat-table] ключ колонки "${clash.key}" конфликтует со служебным полем TableRow ` +
        `(${RESERVED.join(', ')}). Переименуйте колонку.`);
    }
  }

  ngAfterContentInit(): void {
    this.rebuildCellTemplates();
    this.cellDefsSub = this.cellDefs.changes.subscribe(() => {
      this.rebuildCellTemplates();
      this.cdr.markForCheck();            // таблица OnPush
    });
    this.rebuildHeaderCellTemplates();
    this.headerCellDefsSub = this.headerCellDefs.changes.subscribe(() => {
      this.rebuildHeaderCellTemplates();
      this.cdr.markForCheck();            // таблица OnPush
    });
    this.defSubs.push(
      this.resolveDef(this.expandedRowDefs, 'ngxAurExpandedRowDef', t => this._expandedRowTpl = t),
      this.resolveDef(this.rowMarkerDefs, 'ngxAurRowMarkerDef', t => this._rowMarkerTpl = t),
      this.resolveDef(this.extraHeaderTopDefs, 'ngxAurExtraHeaderTopDef', t => this._extraHeaderTopTpl = t),
      this.resolveDef(this.extraHeaderBottomDefs, 'ngxAurExtraHeaderBottomDef', t => this._extraHeaderBottomTpl = t),
    );
  }

  /** Пересобирает карту key → шаблон из спроецированных ngxAurCellDef. */
  private rebuildCellTemplates(): void {
    this._cellTemplates.clear();
    const keys = new Set(this.tableConfig.columnsCfg.map(c => c.key));
    this.cellDefs.forEach(def => {
      this._cellTemplates.set(def.key, def.templateRef);   // дубль ключа → побеждает последний
      if (isDevMode() && !keys.has(def.key)) {
        console.warn(`[aur-mat-table] ngxAurCellDef="${def.key}" не соответствует ни одной колонке.`);
      }
    });
    // navKeys зависят от кастомных ключей (custom-ячейки навигационны только через includeColumns)
    this.rowLinkProvider.init(this._displayColumns, new Set(this._cellTemplates.keys()));
  }

  /**
   * Резолвит одно-слотовую template-директиву: ставит первую в assign, dev-warn при
   * нескольких, подписывается на .changes (OnPush). Зеркало механизма cellDefs.
   */
  private resolveDef<C>(
    list: QueryList<{ templateRef: TemplateRef<C> }>,
    selector: string,
    assign: (tpl: TemplateRef<C> | null) => void,
  ): Subscription {
    const apply = () => {
      if (isDevMode() && list.length > 1) {
        console.warn(`[aur-mat-table] обнаружено несколько ${selector}; используется первый.`);
      }
      assign(list.first?.templateRef ?? null);
      this.cdr.markForCheck();            // таблица OnPush
    };
    apply();
    return list.changes.subscribe(apply);
  }

  /** Контекст row-level шаблона (обогащённый, как AurCellContext минус value). */
  rowCtx(element: TableRow<T>): AurRowContext<T> {
    return { $implicit: element.rowSrc, row: element, rowSrc: element.rowSrc, index: element.rowId };
  }

  /** Контекст кастомного шаблона ячейки (пересобирается в CD). */
  cellCtx(element: TableRow<T>, key: string): AurCellContext<T> {
    const value = element[key];
    return { $implicit: value, value, row: element, rowSrc: element.rowSrc, index: element.rowId };
  }

  /** Пересобирает карту key → деф заголовка из спроецированных ngxAurHeaderCellDef. */
  private rebuildHeaderCellTemplates(): void {
    this._headerCellDefs.clear();
    const keys = new Set(this.tableConfig.columnsCfg.map(c => c.key));
    this.headerCellDefs.forEach(def => {
      this._headerCellDefs.set(def.key, def);   // дубль ключа → побеждает последний
      if (isDevMode() && !keys.has(def.key)) {
        console.warn(`[aur-mat-table] ngxAurHeaderCellDef="${def.key}" не соответствует ни одной колонке.`);
      }
    });
  }

  /** Шаблон заголовка для колонки (или null). */
  headerTpl(key: string): TemplateRef<AurHeaderCellContext<T>> | null {
    return this._headerCellDefs.get(key)?.templateRef ?? null;
  }

  /** Шаблон заголовка забрал ячейку (ownsCell) — mat-sort-header не рендерится. */
  private headerOwnsCell(key: string): boolean {
    return !!this._headerCellDefs.get(key)?.ownsCell;
  }

  /** Рендерить mat-sort-header, если колонка сортируема И шаблон не забрал ячейку (ownsCell). */
  isHeaderSortHeader(col: ColumnConfig<T>): boolean {
    return isFeatureEnabledFn(col.sort) && !this.headerOwnsCell(col.key);
  }

  /** Контекст header-шаблона (пересобирается в CD, как cellCtx). */
  headerCtx(col: ColumnConfig<T>): AurHeaderCellContext<T> {
    const key = col.key;
    const active = this.matSort?.active === key;
    return {
      $implicit: col,
      column: col,
      key,
      sort: {
        sortable: isFeatureEnabledFn(col.sort),
        active,
        direction: active ? this.matSort.direction : '',
        toggle: () => this.matSort?.sort({ id: key, start: 'asc', disableClear: false }),
      },
      filter: {
        apply: f => this.applyFilter(key, f),
        remove: () => this.removeFilter(key),
        active: this.filterStorage.has(key),
      },
    };
  }

  // нам это нужно, чтобы пагинация работала с *ngIf
  ngAfterViewInit(): void {
    // Должно оставаться безусловным — также покрывает случай первого изменения externalPaginator
    // для клиентского режима (ngOnChanges откладывает initPaginator() при firstChange).
    this.initPaginator()
    this.initSortingDataAccessor();
    // SSR: на сервере ResizeObserver не определён — фича columnOffsets работает только в браузере
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeColumnOffsetsObserver = new ResizeObserver(() => this.updateColumnOffsets());
      this.resizeColumnOffsetsObserver.observe(this.table.nativeElement);
    }
    if (this.hasPageSource()) {
      this.startServerController();
    }
  }


  private initPaginator(): void {
    // В серверном режиме держим null — MatTableDataSource вызвал бы
    // _updatePaginator(filteredDataLength) и перезаписал бы длину, присланную сервером.
    // ?? null нормализует undefined (пагинатор не отрендерен), чтобы гвард был точным.
    const target = this.isServerMode() ? null : (this.activePaginator ?? null);
    if (this.tableDataSource.paginator !== target) {
      // сеттер пересоздаёт внутреннюю подписку — присваиваем только при реальном изменении
      this.tableDataSource.paginator = target;
    }
  }

  private initSortingDataAccessor(): void {
    // Серверная обвязка: сортирует сервер — не привязываем MatSort к dataSource, иначе
    // _orderData пересортировал бы серверную страницу по значениям valueConverter
    // (зеркало initPaginator(), который по той же причине не привязывает пагинатор).
    // Стрелки и matSortChange живут на директиве MatSort и от привязки не зависят.
    const sort = this.isServerMode() ? null : (this.matSort ?? null);
    if (this.tableDataSource.sort !== sort) {
      // тот же гвард: сеттер .sort тоже пересоздаёт подписку
      this.tableDataSource.sort = sort;
    }
    // обычное свойство, подписку не трогает — присваиваем без гварда
    this.tableDataSource.sortingDataAccessor = (data, key) => {
      const customSortFunction = this.customSortFunctions.get(key);
      return customSortFunction ? customSortFunction(data, key) : data[key];
    };
  }

  private updateColumnOffsets() {
    const tableEl: HTMLElement | undefined = this.table?.nativeElement;
    if (!tableEl) {
      return;
    }
    const offsets: ColumnOffset[] = Array.from(tableEl.querySelectorAll('th'))
      .slice(0, this._displayColumns.length)
      .map((c) => (c as HTMLElement))
      .map((c, index) => ({
        left: c.offsetLeft,
        width: c.offsetWidth,
        key: this._displayColumns[index]
      }));
    if (OffsetUtil.areNotEqual(this.prevColumnOffsets, offsets)) {
      this.prevColumnOffsets = offsets;
      this.columnOffsets.emit(offsets);
    }
  }

  private prepareTableData(initSelection: T[] = []) {
    // карту кастомных сортировок обновляем ДО initTable(): sorting accessor читает её
    // синхронно во время sort-прохода пайплайна, который запускает присваивание .data=
    this.initCustomSortFunctionsMap();
    this.initTable();
    this.removeWrongKeysFromDisplayColumns();
    // initPaginator() идемпотентен (гвард по target) и сам держит paginator=null в server-режиме —
    // отдельная проверка paginatorState больше не нужна.
    this.initPaginator();
    this.initSortingDataAccessor();

    this.indexProvider = IndexProvider.create(this.tableConfig)
      .addIndexColumn(this._displayColumns);

    this.rowActionsProvider = RowActionProvider.create(this.tableConfig)
      .addActionColumns(this._displayColumns)
      .setView(this.tableDataSource.data);

    this.selectionProvider = SelectionProvider.create(this.tableConfig, this.tableDataSource, initSelection)
      .addCheckboxColumn(this._displayColumns)
      .bindEventEmitters(this.selectChange, this.selectAdded, this.selectRemoved, this.selectionModel);

    this.paginationProvider = PaginationProvider.create(this.tableConfig);

    // Серверная страница содержит только свои строки (id = позиция в странице) — смещаем индекс
    // на номер страницы. Клиентский режим режет весь датасет локально (id сквозной) → offset 0.
    const pageSize = this.activePaginator?.pageSize ?? this.paginationProvider.size;
    this._indexPageOffset = (this.isServerMode() && this.paginatorState) ? this.paginatorState.pageIndex * pageSize : 0;

    this.totalRowProvider = TotalRowProvider.create(this.tableConfig, this.tableDataSource)
      .setTotalRow();

    const _totals = this.totalRowProvider.totals;
    const _data = this.tableDataSource.data;
    const _sc = this.tableConfig.totalRowCfg?.styleCfg;
    this._totalStyle = this.toCss(this.resolveTotal(_sc?.style, _totals, _data) ?? null);
    this._totalClass = this.resolveTotal(_sc?.class, _totals, _data) ?? null;

    this.headerButtonProvider = new HeaderButtonProvider(this.tableConfig.headerButtonCfg)

    this.dragDropProvider = DragDropProvider.create(this.viewContainerRef, this.tableConfig)
      .addColumn(this._displayColumns);

    // Timeline ПОСЛЕДНИМ — unshift гарантирует позицию 0 после всех остальных провайдеров
    this.timelineProvider = TimelineProvider.create(this.tableConfig)
      .addTimelineColumn(this._displayColumns);

    // Фаза 2: anchor before/after — все возможные якоря (data, спец, action-start/end) уже на местах.
    // До построения extra-header/detail-ремапа (ниже), чтобы они охватили финальный порядок.
    this.rowActionsProvider.applyAnchors(this._displayColumns);

    // rowLink после applyAnchors: primary-колонка выбирается по финальному порядку _displayColumns
    this.rowLinkProvider = RowLinkProvider.create(this.tableConfig)
      .init(this._displayColumns, new Set(this._cellTemplates.keys()));

    this.emitFilteredValues();

    this._displayExtraHeaderTopCell = this._displayColumns.map(col => col + this.EXTRA_HEADER_CELL_TOP_SUFFIX)
    this._displayExtraHeaderBottomCell = this._displayColumns.map(col => col + this.EXTRA_HEADER_CELL_BOTTOM_SUFFIX)
    this.remapExpandedToData();
  }

  private removeWrongKeysFromDisplayColumns() {
    const whiteKeys = new Set<string>([
      ...this.tableConfig.columnsCfg.map(cfg => cfg.key),
      ...NgxAurTableConfigUtil.actionColumnNames(this.tableConfig),
    ]);
    this._displayColumns = this._displayColumns.filter(actual => whiteKeys.has(actual) || actual.startsWith('tbl_'));
  }

  private initCustomSortFunctionsMap() {
    this.customSortFunctions.clear();
    this.tableConfig.columnsCfg
      .filter(c => c.sort != null && isFeatureEnabledFn(c.sort) && c.sort.customSort)
      .forEach(c => this.customSortFunctions.set(c.key, c.sort!.customSort!))
  }

  private initTable() {
    this._tableName = this.tableConfig.name ?? 'unknown-table-name';
    this.tableDataSource.data = TableRowsFactory.convert(this.tableData, this.tableConfig.columnsCfg);
    this.tableView = TableViewFactory.toView(this.tableDataSource.data, this.tableConfig)
    this.rowStyles = RowStyleFactory.toRowStyles(this.tableDataSource.data, this.tableConfig)
    this._headerStyle = this.toCss(this.tableConfig.headerRowCfg?.styleCfg?.style);
    this._headerClass = this.tableConfig.headerRowCfg?.styleCfg?.class ?? null;
    this._alignClass = this.buildAlignClassMap();
    this._rowsInteractive = isFeatureEnabledFn(this.tableConfig.bodyRowCfg?.clickCfg);
    if (!this._customDisplayColumnsEnabled) {
      this._displayColumns = DisplayColumnsFactory.create(this.tableConfig);
    }
  }

  private buildAlignClassMap(): Record<string, 'aur-align-center' | 'aur-align-right' | undefined> {
    const toClass = (a?: ColumnAlign) =>
      a === 'center' ? 'aur-align-center' as const
        : a === 'right' ? 'aur-align-right' as const
          : undefined;
    const map: Record<string, 'aur-align-center' | 'aur-align-right' | undefined> = {};
    const def = this.tableConfig.tableViewCfg?.align;
    this.tableConfig.columnsCfg.forEach(c => map[c.key] = toClass(c.align ?? def));
    map[IndexProvider.COLUMN_NAME] = toClass(this.tableConfig.indexCfg?.align ?? def);
    return map;
  }

  applySearchFilter(event: Event) {
    this._searchText = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.applyAllFilters();
  }

  public removeFilter(filterName: string) {
    if (this.filterStorage.get(filterName)) {
      this.filterStorage.delete(filterName);
      this.applyFiltersInternal();
    }
  }

  public applyFilter(filterName: string, filter: NgxAurFilters.Base<T>): void {
    if (!this.filterStorage.get(filterName) || (!this.filterStorage.get(filterName)!.equals(filter))) {
      this.filterStorage.set(filterName, filter);
      this.applyFiltersInternal();
    }
  }

  public clearFilters() {
    this.filterStorage.clear();
    this.applyFiltersInternal();
  }

  private applyFiltersInternal() {
    this.applyAllFilters();
  }

  private applyAllFilters() {
    const filterActions = [...this.filterStorage.values()];
    const searchText = this._searchText;
    const defaultPredicate = this._defaultFilterPredicate;

    this.tableDataSource.filterPredicate = (data, _filter) => {
      const passesCustom = filterActions.length === 0 || filterActions.every(f => f.filterFn()(data));
      const passesSearch = !searchText || defaultPredicate(data, searchText);
      return passesCustom && passesSearch;
    };
    // Передать уникальное значение чтобы фильтрация запустилась
    this.tableDataSource.filter = 'combined-' + Math.random();
    this.emitFilteredValues();
  }

  private emitFilteredValues(): void {
    this.filterChange.emit(this.tableDataSource.filteredData.map(f => f.rowSrc));
    this.updateTimelineBounds();
  }

  sortTable(sortParameters: Sort) {
    this.sort.emit(sortParameters);
    if (this.serverPageController) {
      this.serverPageController.onSort(sortParameters);
    }
    // MatTableDataSource обрабатывает sort через RxJS-подписку —
    // filteredData обновится в следующем микротаске
    Promise.resolve().then(() => {
      this.updateTimelineBounds();
      this.cdr.markForCheck();
    });
  }

  onPageChangeInternal(event: PageEvent): void {
    this.updateTimelineBounds();
    this.pageChange.emit(event);
    if (this.serverPageController) {
      this.serverPageController.onPage({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    }
    this.cdr.markForCheck();
  }

  private updateTimelineBounds(): void {
    if (!this.timelineProvider.isEnabled) return;

    const visibleData = this.getTimelineVisibleData();
    const segmentColorFn = this.timelineProvider.segmentColor;

    const isDefaultOrder = !this.matSort?.active || this.matSort?.direction === '';
    const hasActiveFilter = this.tableDataSource.filteredData.length
      < this.tableDataSource.data.length;
    const detectGaps = isDefaultOrder && hasActiveFilter;

    this._timelineGaps.clear();

    for (let i = 0; i < visibleData.length; i++) {
      const topColor = segmentColorFn && i > 0
        ? segmentColorFn(visibleData[i - 1], visibleData[i])
        : null;
      const bottomColor = segmentColorFn && i < visibleData.length - 1
        ? segmentColorFn(visibleData[i], visibleData[i + 1])
        : null;

      this._timelineGaps.set(visibleData[i].rowId, {
        topGap: detectGaps && i > 0
          && visibleData[i].rowId !== visibleData[i - 1].rowId + 1,
        bottomGap: detectGaps && i < visibleData.length - 1
          && visibleData[i].rowId !== visibleData[i + 1].rowId - 1,
        topColor,
        bottomColor
      });
    }

    this._timelineFirstId = visibleData.length > 0 ? visibleData[0].rowId : -1;
    this._timelineLastId = visibleData.length > 0 ? visibleData[visibleData.length - 1].rowId : -1;
  }

  private getTimelineVisibleData(): TableRow<T>[] {
    const data = this.tableDataSource.filteredData;

    // Server-side: данные уже постраничные, не режем повторно
    if (this.isServerMode()) return data;

    // Client-side с пагинацией: вырезаем видимый срез
    if (this.paginationProvider.isEnabled && this.activePaginator) {
      const start = this.activePaginator.pageIndex * this.activePaginator.pageSize;
      return data.slice(start, start + this.activePaginator.pageSize);
    }

    // Без пагинации
    return data;
  }

  emitSelectedRowsAction(action: string, rows: T[]) {
    this.selectedRowsAction.emit({action, value: rows});
  }

  emitRowAction(action: string, row: T, $event: MouseEvent) {
    $event.stopPropagation();
    this.rowAction.emit({action, value: row});
  }

  /**
   * Отправляет действие, инициированное элементом mat-menu.
   *
   * В отличие от {@link emitRowAction}, здесь НЕ нужно вызывать $event.stopPropagation():
   * mat-menu закрывается, когда клик всплывает до его панели
   * ((click)="closed.emit('click')"), поэтому остановка всплытия оставила бы
   * меню открытым. Меню рендерится в overlay вне строки, так что здесь нет
   * клика по строке, который нужно подавлять.
   */
  emitMenuAction(action: string, row: T) {
    this.rowAction.emit({action, value: row});
  }

  masterToggle() {
    this.selectionProvider.masterToggle();
  }

  isAllSelected(): boolean {
    return this.selectionProvider.isAllSelected();
  }

  castSrc(row: any): TableRow<T> {
    return row;
  }

  /** trackBy для всех дефов строк (row defs) таблицы: бизнес-ключ из конфига или ссылка на rowSrc. */
  trackByRow = (_: number, row: TableRow<T>): unknown =>
    this.tableConfig.trackBy ? this.tableConfig.trackBy(row.rowSrc) : row.rowSrc;

  /** trackBy для *ngFor по action-колонкам: columnName стабилен/уникален (RowActionProvider
   *  дедупит ключи), поэтому MatColumnDef переиспользуется между сменами [tableData] —
   *  пересозданный провайдер обновляет col на месте, а не пересоздаёт def (иначе уже
   *  отрисованные ячейки держат старый col → устаревший/пустой actionView). Фикс #1 (иконки
   *  не обновляются) и #3 (пустые ячейки при async-первой загрузке). */
  trackByActionColumn = (_: number, col: ActionColumnView<T>): string => col.columnName;

  /** StyleBuilder.Row | string | null -> CSS-строка | null. */
  private toCss(s?: StyleBuilder.Row | string | null): string | null {
    if (s == null) return null;
    return typeof s === 'string' ? s : s.build();
  }

  /** `base` с `overlay` поверх. Builder-ы -> переопределение полей; любая строка -> конкатенация (в CSS побеждает последнее). */
  private mergeStyle(
    base?: StyleBuilder.Row | string | null,
    overlay?: StyleBuilder.Row | string | null,
  ): string | null {
    if (base == null) return this.toCss(overlay);
    if (overlay == null) return this.toCss(base);
    if (base instanceof StyleBuilder.Row && overlay instanceof StyleBuilder.Row) {
      return base.overrideWith(overlay).build();
    }
    return `${this.toCss(base) ?? ''} ${this.toCss(overlay) ?? ''}`.trim();
  }

  /** хук итога: статическое значение или (totals, data) => значение. */
  private resolveTotal<R>(
    v: R | ((t: Map<string, any>, d: TableRow<T>[]) => R) | undefined,
    totals: Map<string, any>, data: TableRow<T>[],
  ): R | undefined {
    return typeof v === 'function' ? (v as any)(totals, data) : v;
  }

  /** RowValue<T,R> → R: статика как есть, функция вызывается со строкой. */
  private resolveRow<R>(v: RowValue<T, R> | undefined, row: TableRow<T>): R | undefined {
    return typeof v === 'function' ? (v as (row: TableRow<T>) => R)(row) : v;
  }

  /** Хелпер для шаблона: функция активна, когда её конфигурация присутствует, если только не задано `enable: false`. */
  isFeatureEnabled(cfg: { enable?: boolean } | null | undefined): boolean {
    return isFeatureEnabledFn(cfg);
  }

  /** Цвет иконки действия/пункта меню: при disabled — null, чтобы не перекрывать
   *  своим icon.color нейтральный disabled-цвет Material (иначе disabled-кнопка
   *  выглядит активной). Принимает и Action, и MenuItem (структурно). */
  iconColorOf(el: { disabled?: boolean; icon?: { color?: string } }): string | null {
    return el.disabled ? null : (el.icon?.color ?? null);
  }

  /**
   * Видна ли строка итогов на текущей странице.
   * По умолчанию итоги показываются только на последней странице пагинации;
   * `totalRowCfg.showOnEveryPage: true` возвращает показ на каждой странице.
   * Когда пагинация выключена — итоги показываются всегда.
   */
  isTotalRowVisible(): boolean {
    if (this.tableConfig.totalRowCfg?.showOnEveryPage) return true;
    if (!this.paginationProvider.isEnabled) return true;
    const { pageIndex, lastPageIndex } = this.currentPaging();
    return pageIndex >= lastPageIndex;
  }

  /**
   * Текущий индекс страницы и индекс последней страницы.
   * Серверный режим читает их из paginatorState (как getTimelineVisibleData),
   * клиентский — из активного пагинатора и числа отфильтрованных строк.
   */
  private currentPaging(): { pageIndex: number; lastPageIndex: number } {
    let total: number, pageIndex: number, pageSize: number;
    if (this.isServerMode()) {
      const st = this.paginatorState ?? PaginatorState.empty();
      total = st.length;
      pageIndex = st.pageIndex;
      pageSize = this.activePaginator?.pageSize ?? this.paginationProvider.size;
    } else {
      total = this.tableDataSource.filteredData.length;
      pageIndex = this.activePaginator?.pageIndex ?? 0;
      pageSize = this.activePaginator?.pageSize ?? this.paginationProvider.size;
    }
    const lastPageIndex = pageSize > 0 ? Math.max(0, Math.ceil(total / pageSize) - 1) : 0;
    return { pageIndex, lastPageIndex };
  }

  private hoverActive(row: TableRow<T>): boolean {
    const h = this.tableConfig.bodyRowCfg?.hoverCfg;
    return this.hovered === row && h?.enable !== false;
  }

  onRowEnter(row: TableRow<T>) { this.hovered = row; }
  onRowLeave(row: TableRow<T>) { if (this.hovered === row) this.hovered = null; }

  /** tabindex строки: у ссылочной строки фокус держит якорь (null на <tr>); иначе штатно. */
  rowTabindex(row: TableRow<T>): 0 | null {
    if (this.rowLinkProvider.isLinkRow(row)) return null;
    return this._rowsInteractive ? 0 : null;
  }

  /** [style] для <tr> тела: base -> overlay наведения -> overlay подсветки (побеждает подсветка). */
  rowStyle(row: TableRow<T>): string | null {
    let acc: StyleBuilder.Row | string | null = this.rowStyles[row.rowId]?.style ?? null;
    if (this.hoverActive(row)) {
      acc = this.mergeStyle(acc, this.resolveRow(this.tableConfig.bodyRowCfg?.hoverCfg?.styleCfg?.style, row) ?? null);
    }
    if (this.highlighted === row.rowSrc) {
      const sc = this.resolvedHighlightStyleCfg();
      acc = this.mergeStyle(acc, this.resolveRow(sc?.style, row) ?? null);
    }
    return this.toCss(acc);
  }

  rowNgClass(row: TableRow<T>): { [klass: string]: boolean } {
    const hover = this.tableConfig.bodyRowCfg?.hoverCfg;
    const isHighlighted = this.highlighted === row.rowSrc;
    // highlight-style резолвим только для подсвеченной строки (иначе функция зря зовётся на каждую)
    const hlStyleCfg = isHighlighted ? this.resolvedHighlightStyleCfg() : undefined;
    const hl = isHighlighted ? this.resolveRow(hlStyleCfg?.style, row) : null;
    const hlHasColor = hl instanceof StyleBuilder.Row ? !!hl.colorValue : !!hl;
    const cls: { [klass: string]: boolean } = {
      'pointer': this.resolveRow(hover?.pointer, row) || false,
      'new-color': isHighlighted && hlHasColor,
    };
    const custom = this.rowStyles[row.rowId]?.class;
    if (custom) cls[custom] = true;
    const hcls = this.hoverActive(row) ? this.resolveRow(hover?.styleCfg?.class, row) : null;
    if (hcls) cls[hcls] = true;
    const ccls = isHighlighted ? this.resolveRow(hlStyleCfg?.class, row) : null;
    if (ccls) cls[ccls] = true;
    return cls;
  }

  /** Источник highlight-стиля. */
  private resolvedHighlightStyleCfg(): HighlightStyleConfig<T> | undefined {
    return this.tableConfig.bodyRowCfg?.highlightCfg?.styleCfg;
  }

  /** cancelable подсветки. */
  private resolvedHighlightCancelable(): boolean {
    return this.tableConfig.bodyRowCfg?.highlightCfg?.cancelable ?? false;
  }

  /** Применяет [highlightedRow] во внутреннее состояние (controlled/manual + первый seed row-click). */
  private syncHighlightFromInput(): void {
    this.highlighted = this.highlightedRow ?? undefined;
    if (this.highlighted !== undefined) this.scrollHighlightedIntoView(this.highlighted);
  }

  /** Прокрутка строки с данным rowSrc в видимую область (smooth/center). */
  private scrollHighlightedIntoView(src: T): void {
    const index = this.tableDataSource.data.findIndex(row => row.rowSrc === src);
    if (index >= 0) {
      this.rows?.toArray()[index]?.nativeElement.scrollIntoView({
        behavior: "smooth", block: "center", inline: "center",
      });
    }
  }

  handleRowClick(row: TableRow<T>) {
    // clickCfg.enable: false — строка полностью неинтерактивна (Group 2 opt-out):
    // ни rowClick, ни highlight, ни авто-раскрытие. Покрывает и клавиатуру (handleRowKeydown делегирует сюда).
    if (this.tableConfig.bodyRowCfg?.clickCfg?.enable === false) return;

    // Ссылочная строка: навигацию выполняет якорь (<a routerLink>);
    // rowClick/highlight/expand по клику подавляем. Строки без ссылки идут дальше штатно.
    if (this.rowLinkProvider.isLinkRow(row)) return;

    const mode = this.tableConfig.bodyRowCfg?.highlightCfg?.mode ?? 'row-click';
    const cancelable = this.resolvedHighlightCancelable();
    const src = row.rowSrc;
    const toggleOff = src === this.highlighted && cancelable;

    if (mode === 'manual') {
      this.rowClick.emit(src);                 // только action-намерение; highlight не трогаем
      this.handleExpandOnClick(row);
      return;
    }

    this.rowClick.emit(toggleOff ? undefined : src);

    if (mode === 'controlled') {
      this.highlightedRowChange.emit(toggleOff ? null : src);   // запрос, без мутации
    } else {                                                    // row-click
      this.highlighted = toggleOff ? undefined : src;
      this.highlightedRowChange.emit(this.highlighted ?? null);
    }

    this.handleExpandOnClick(row);
  }

  /**
   * Клавиатурная активация строки: Enter/Space ведут себя как клик.
   * Обрабатываются только события самой строки — Enter/Space на вложенных
   * интерактивных элементах (чекбокс, кнопки действий) всплывают и не должны
   * дублировать клик по строке. preventDefault у Space подавляет скролл страницы.
   */
  handleRowKeydown(event: KeyboardEvent, row: TableRow<T>) {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleRowClick(row);
    }
  }

  /** Ключ идентичности раскрытия по строке таблицы — зеркало trackByRow. */
  private expandKey(row: TableRow<T>): unknown {
    return this.tableConfig.trackBy ? this.tableConfig.trackBy(row.rowSrc) : row.rowSrc;
  }

  /** Ключ по исходному значению (инпуты приходят как rowSrc, а не TableRow). */
  private keyOfSrc(src: T): unknown {
    return this.tableConfig.trackBy ? this.tableConfig.trackBy(src) : src;
  }

  /** Рендер-предикат detail-строки (используется шаблоном). */
  isExpanded(row: TableRow<T>): boolean {
    return this._expanded.has(this.expandKey(row));
  }

  /** Следующее состояние раскрытия при клике/запросе по строке. */
  private nextExpanded(row: TableRow<T>): Map<unknown, T> {
    const key = this.expandKey(row);
    const multiple = !!this.tableConfig.extendedRowCfg?.multiple;
    const next = new Map(this._expanded);
    if (next.has(key)) {
      next.delete(key);                 // повторный клик по открытой — закрыть
    } else {
      if (!multiple) next.clear();      // single → аккордеон
      next.set(key, row.rowSrc);
    }
    return next;
  }

  /** Эмит активной пары по флагу multiple. */
  private emitExpanded(map: Map<unknown, T>): void {
    if (this.tableConfig.extendedRowCfg?.multiple) {
      this.expandedRowsChange.emit([...map.values()]);
    } else {
      this.expandedRowChange.emit([...map.values()][0] ?? null);
    }
  }

  /** Реакция на клик по строке (вызывается из handleRowClick после гейта enable). */
  private handleExpandOnClick(row: TableRow<T>): void {
    if (!this.expandedRowTemplate) return;
    const mode = this.tableConfig.extendedRowCfg?.mode ?? 'row-click';
    if (mode === 'manual') return;                       // клик инертен для раскрытия
    if (mode === 'controlled') {
      this.emitExpanded(this.nextExpanded(row));         // только эмит, без мутации
      return;
    }
    this._expanded = this.nextExpanded(row);             // row-click: мутируем + эмитим
    this.emitExpanded(this._expanded);
  }

  /** На смене данных: обновляет rowSrc по совпадающим ключам и выкидывает исчезнувшие. */
  private remapExpandedToData(): void {
    if (this._expanded.size === 0) return;
    const byKey = new Map<unknown, T>();
    this.tableDataSource.data.forEach(r => byKey.set(this.expandKey(r), r.rowSrc));
    const next = new Map<unknown, T>();
    this._expanded.forEach((_src, key) => {
      if (byKey.has(key)) next.set(key, byKey.get(key)!);
    });
    this._expanded = next;
  }

  /** Перестраивает _expanded из активного инпута (выбор пары по multiple). */
  private syncExpandedFromInputs(multiple: boolean): void {
    const next = new Map<unknown, T>();
    if (multiple) {
      (this.expandedRows ?? []).forEach(src => next.set(this.keyOfSrc(src), src));
      if (isDevMode() && this.expandedRow != null) {
        console.warn('[aur-mat-table] multiple:true — используйте [expandedRows], [expandedRow] игнорируется.');
      }
    } else {
      if (this.expandedRow != null) next.set(this.keyOfSrc(this.expandedRow), this.expandedRow);
      if (isDevMode() && this.expandedRows?.length) {
        console.warn('[aur-mat-table] multiple:false — используйте [expandedRow], [expandedRows] игнорируется.');
      }
    }
    this._expanded = next;
  }

  public getSelectionModel(): SelectionModel<T> {
    return this.selectionProvider.selection;
  }

  /** Server contract: no client paginator/sort binding, no re-slicing, server index offset/timeline. */
  private isServerMode(): boolean {
    return this.tableConfig?.paginationCfg?.mode === 'server' || !!this.pageSource;
  }

  /** Table owns the load loop (declarative). Gates ServerPageController only. */
  private hasPageSource(): boolean {
    return !!this.pageSource;
  }

  /** Dev-mode guards for contradictory pagination wiring. */
  private warnPaginationModeMisconfig(): void {
    if (!isDevMode()) return;
    if (this.tableConfig.paginationCfg?.mode === 'client' && this.pageSource) {
      console.warn('[aur-mat-table] paginationCfg.mode:"client" игнорируется при заданном [pageSource] — режим серверный.');
    }
    if (this.isServerMode() && this.externalPaginator && !this.hasPageSource()) {
      console.warn('[aur-mat-table] внешний пагинатор в ручном серверном режиме не поддерживается; используйте [pageSource] или встроенный пагинатор.');
    }
  }

  private startServerController(): void {
    if (!this.pageSource) {
      return;
    }
    this.serverPageController = new ServerPageController<T>(this.pageSource, {
      onResult: result => {
        this.paginatorState = result.state;
        this.applyExternalPaginatorState(result.state);
        this.tableData = result.content;
        this.refreshTable();
        // эмит ПОСЛЕ refreshTable(): подписчик читает уже применённое публичное состояние таблицы
        this.pageLoaded.emit({
          content: result.content,
          totalElements: result.state.length,
          pageIndex: result.state.pageIndex,
        });
        this.cdr.markForCheck();
      },
      onLoading: loading => {
        this.loadingChange.emit(loading);
        this.cdr.markForCheck();
      },
      onError: error => this.pageError.emit(error),
    });

    this.subscribeExternalPaginator();

    const initialSort: Sort | undefined =
      this.matSort?.active ? { active: this.matSort.active, direction: this.matSort.direction } : undefined;

    this.serverPageController.start({
      // провайдер может быть ещё не инициализирован (в серверном режиме нет привязки tableData) — читаем из конфигурации
      pageSize: this.tableConfig.paginationCfg?.size ?? 20,
      sort: initialSort,
    });
  }

  private subscribeExternalPaginator(): void {
    this.externalPaginatorSub?.unsubscribe();
    if (this.externalPaginator) {
      this.externalPaginatorSub = this.externalPaginator.page.subscribe(event =>
        this.onPageChangeInternal(event)
      );
    }
  }

  private applyExternalPaginatorState(state: PaginatorState): void {
    if (this.externalPaginator) {
      // РИСК (подход C из спецификации): OnPush MatPaginator требует CD, чтобы отразить императивные изменения.
      this.externalPaginator.length = state.length;
      this.externalPaginator.pageIndex = state.pageIndex;
      this.cdr.markForCheck();
    }
  }

  /** Повторно вызывает pageSource (серверный режим). resetPageIndex по умолчанию true (например, изменился внешний фильтр). */
  public reload(opts?: { resetPageIndex?: boolean }): void {
    if (this.serverPageController) {
      this.serverPageController.reload(opts);
    } else {
      // Клиентский режим: повторно применяем текущие данные/фильтры.
      this.refreshTable();
    }
  }

  ngOnDestroy() {
    this.resizeColumnOffsetsObserver?.disconnect();
    this.serverPageController?.stop();
    this.externalPaginatorSub?.unsubscribe();
    this.cellDefsSub?.unsubscribe();
    this.headerCellDefsSub?.unsubscribe();
    this.defSubs.forEach(s => s.unsubscribe());
  }

  onDragStart($event: DragEvent, row: TableRow<T>) {
    if (this.selectionProvider.isEnabled && this.dragDropProvider.multiple && this.selectionProvider.selection.selected.length > 1) {
      let selectedRows = this.selectionProvider.getSelectedRows();
      if (selectedRows.find(r => r.rowId === row.rowId)) {
        this.dragDropProvider.manager.startDrag(this._tableName, selectedRows, $event);
        return;
      }
    }
    this.dragDropProvider.manager.startDrag(this._tableName, [row], $event);
  }

  onDragOver($event: DragEvent) {
    this.dragDropProvider.manager.canDropPreventDefault(this._tableName, $event);
  }

  onDrop($event: DragEvent, row: TableRow<T>) {
    this.dragDropProvider.manager.drop(this._tableName, row);
  }

  onDragEnd($event: DragEvent, row: TableRow<T>) {
    this.dragDropProvider.manager.endDrag();
  }
}
